import { useCallback, useMemo, useState } from 'react'

import { F0Dialog } from '@factorialco/f0-react'
import {
  AddMembersToPostsGroupDocument,
  PostsGroupHeaderDocument,
  RemoveMembersFromPostsGroupDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import type { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'

export type Action = 'add' | 'remove'

interface Props {
  onClose: () => void
  groupId: string
  action: Action
}

function ManageMembersGroup({ groupId, action, onClose }: Props) {
  const [isOpen, setIsOpen] = useState(true)
  const [running, setRunning] = useState(false)
  const [employeeSelection, setEmployeeSelection] = useState<EmployeeSelectorV2MultiSelectState>({
    type: 'manual',
    ids: [],
  })

  const { data, loading } = useQuery(PostsGroupHeaderDocument, {
    variables: { id: groupId },
  })

  const handleClose = useCallback(() => {
    setIsOpen(false)
    setTimeout(onClose, 100)
  }, [onClose])

  const currentMemberEmployeeIds = useMemo(() => {
    return data?.posts?.group?.employees?.map((employee) => employee.id) ?? []
  }, [data])

  const [addMembers] = useMutation(AddMembersToPostsGroupDocument, {
    extractorFn: (mutationData) => mutationData.postsMutations.addMembersPostsGroup,
  })

  const [removeMembers] = useMutation(RemoveMembersFromPostsGroupDocument, {
    extractorFn: (mutationData) => mutationData.postsMutations.removeMembersPostsGroup,
  })

  const handleEmployeeChange = useCallback((state: EmployeeSelectorV2MultiSelectState) => {
    setEmployeeSelection(state)
  }, [])

  const handleSubmit = async () => {
    if (employeeSelection.type === 'manual' && employeeSelection.ids.length === 0) return

    setRunning(true)

    const mutationOptions = {
      variables: {
        id: groupId,
        employeeIds: [],
        employeeSelection: employeeSelection,
      },
      refetchQueries: [{ query: PostsGroupHeaderDocument, variables: { id: groupId } }],
      awaitRefetchQueries: true,
    }

    const onError = () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('posts.communities.error_message'),
      })
    }

    let success = false

    if (action === 'add') {
      const result = await addMembers(mutationOptions)
      const handled = handleResult(result, onError)
      success = handled.success
    } else {
      const result = await removeMembers(mutationOptions)
      const handled = handleResult(result, onError)
      success = handled.success
    }

    setRunning(false)

    if (success) {
      handleClose()
    }
  }

  const removeModalInfo = {
    title: i18n.t('dashboard.groups.remove_members.title'),
    description: i18n.t('dashboard.groups.remove_members.description'),
    submit: i18n.t('dashboard.groups.remove_members.submit'),
  }

  const addModalInfo = {
    title: i18n.t('dashboard.groups.add_members.title'),
    description: i18n.t('dashboard.groups.add_members.description'),
    submit: i18n.t('dashboard.groups.add_members.submit'),
  }

  const modalInfo = action === 'remove' ? removeModalInfo : addModalInfo

  const forcedFilters = useMemo(() => {
    if (action === 'add') {
      return {
        excludeIds: currentMemberEmployeeIds,
        excludeCurrentEmployee: true,
      }
    }
    return {
      employeeIds: currentMemberEmployeeIds,
      excludeCurrentEmployee: true,
    }
  }, [action, currentMemberEmployeeIds])

  return (
    <F0Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title={modalInfo.title}
      width="sm"
      description={modalInfo.description}
      primaryAction={{
        label: modalInfo.submit,
        onClick: handleSubmit,
        disabled:
          (employeeSelection.type === 'manual' && employeeSelection.ids.length === 0) || running,
        loading: running,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: handleClose,
        disabled: running,
      }}
      loading={loading}
    >
      <EmployeeSelectorV2
        multiple
        onChange={handleEmployeeChange}
        forcedFilters={forcedFilters}
        options={{ disableSelectAll: false, asList: true, hideLabel: true }}
      />
    </F0Dialog>
  )
}

export default ManageMembersGroup
